/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/components/auth/ProfileGuard.tsx
 * Release: v2.0.0 - Guard per completamento profilo obbligatorio
 */

import React, { useEffect } from 'react';
import { useAuth } from './AuthProvider';
import { Navigate } from 'react-router-dom';

interface ProfileGuardProps {
  children: React.ReactNode;
}

const ProfileGuard: React.FC<ProfileGuardProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  useEffect(() => {
    // Effetto per debugging o future implementazioni
  }, [isAuthenticated, user]);

  // Mentre carica, mostra loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Caricamento...</div>
      </div>
    );
  }

  // Se non autenticato, reindirizza al login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Se deve completare il profilo, gestisci in base a status/needs_profile_completion
  const needsCompletion = user?.needs_profile_completion ||
                          user?.status === 'pending_completion' ||
                          user?.group_id === 'pending_profile_completion';

  if (needsCompletion) {
    console.log('🚨 [ProfileGuard] Utente deve completare profilo:', {
      userId: user?.id,
      status: user?.status,
      group_id: user?.group_id,
      needs_profile_completion: user?.needs_profile_completion
    });

    // Se siamo già nella pagina di completamento profilo, non fare redirect
    if (window.location.pathname === '/complete-profile' ||
        window.location.pathname.startsWith('/admin/profile-form')) {
      return <>{children}</>;
    }

    // Redirect automatico per tutti gli utenti che devono completare il profilo
    console.log('🔄 [ProfileGuard] Redirect automatico a /complete-profile');
    return <Navigate to="/complete-profile" replace />;
  }

  // Se tutto ok, mostra il contenuto
  return <>{children}</>;
};

export default ProfileGuard;
